import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { enqueueOfflineRequest } from '../utils/offlineQueue';
import '../styles/Checklists.css';

const INITIAL_FILTERS = { phase_key: '', discipline_key: '', q: '' };
const TASK_DONE_STATUSES = new Set(['completed', 'done', 'closed']);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('tr-TR');
};

const resolveChecklistError = (err, fallbackMessage) => {
  const status = Number(err?.response?.status || 0);
  if (status === 404) {
    return 'Checklist API route bulunamadı. Backend sunucusu eski sürüm olabilir; backend servisini yeniden başlatın.';
  }
  return err?.response?.data?.message || fallbackMessage;
};

const toggleChecklistItems = (items, itemId) =>
  items.map((entry) =>
    Number(entry.id) === Number(itemId)
      ? {
          ...entry,
          is_completed: !Boolean(entry.is_completed),
        }
      : entry
  );

const Checklists = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [lists, setLists] = useState([]);
  const [generalSelectedId, setGeneralSelectedId] = useState(null);
  const [personalSelectedId, setPersonalSelectedId] = useState(null);
  const [generalItems, setGeneralItems] = useState([]);
  const [personalItems, setPersonalItems] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [loadingGeneralItems, setLoadingGeneralItems] = useState(false);
  const [loadingPersonalItems, setLoadingPersonalItems] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  const [generalListForm, setGeneralListForm] = useState({ name: '', description: '' });
  const [personalListForm, setPersonalListForm] = useState({ name: '', description: '' });
  const [generalItemForm, setGeneralItemForm] = useState({ title: '', category: '', due_date: '', priority: 'medium' });
  const [personalItemForm, setPersonalItemForm] = useState({ title: '', category: '', due_date: '', priority: 'medium' });

  const [suggestions, setSuggestions] = useState([]);
  const [phaseOptions, setPhaseOptions] = useState([]);
  const [disciplineOptions, setDisciplineOptions] = useState([]);
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [targetListId, setTargetListId] = useState('');
  const [taskProjects, setTaskProjects] = useState([]);
  const [taskSyncProjectId, setTaskSyncProjectId] = useState('all');
  const [taskSyncOpenItems, setTaskSyncOpenItems] = useState([]);
  const [taskSyncDoneItems, setTaskSyncDoneItems] = useState([]);
  const [taskSyncLoading, setTaskSyncLoading] = useState(false);
  const [taskSyncError, setTaskSyncError] = useState('');

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (error) {
      return {};
    }
  }, []);

  const canToggleTaskStatus = [1, 2, 3].includes(Number(currentUser?.role_id));

  const generalLists = useMemo(() => lists.filter((list) => list.type === 'general'), [lists]);
  const personalLists = useMemo(() => lists.filter((list) => list.type === 'personal'), [lists]);
  const allLists = useMemo(() => [...generalLists, ...personalLists], [generalLists, personalLists]);
  const selectedGeneralList = useMemo(
    () => generalLists.find((item) => Number(item.id) === Number(generalSelectedId)) || null,
    [generalLists, generalSelectedId]
  );
  const selectedPersonalList = useMemo(
    () => personalLists.find((item) => Number(item.id) === Number(personalSelectedId)) || null,
    [personalLists, personalSelectedId]
  );

  const groupedSuggestions = useMemo(() => {
    const map = new Map();
    suggestions.forEach((item) => {
      const phaseGroup = map.get(item.phase) || new Map();
      const disciplineGroup = phaseGroup.get(item.discipline) || [];
      disciplineGroup.push(item);
      phaseGroup.set(item.discipline, disciplineGroup);
      map.set(item.phase, phaseGroup);
    });
    return map;
  }, [suggestions]);

  const categorySuggestions = useMemo(
    () => Array.from(new Set(suggestions.map((item) => `${item.phase} - ${item.discipline}`))),
    [suggestions]
  );

  const fetchLists = useCallback(async () => {
    try {
      setLoadingLists(true);
      const response = await apiClient.get(`${API_BASE_URL}/checklists`);
      const incoming = Array.isArray(response.data) ? response.data : [];
      setLists(incoming);
      setError('');

      const incomingGeneral = incoming.filter((item) => item.type === 'general');
      const incomingPersonal = incoming.filter((item) => item.type === 'personal');

      if (!incomingGeneral.some((item) => Number(item.id) === Number(generalSelectedId))) {
        setGeneralSelectedId(incomingGeneral[0]?.id || null);
      }
      if (!incomingPersonal.some((item) => Number(item.id) === Number(personalSelectedId))) {
        setPersonalSelectedId(incomingPersonal[0]?.id || null);
      }
      if (!targetListId && incoming.length > 0) {
        setTargetListId(String(incoming[0].id));
      }
    } catch (err) {
      setError(resolveChecklistError(err, 'Checklist listesi yüklenemedi.'));
    } finally {
      setLoadingLists(false);
    }
  }, [generalSelectedId, personalSelectedId, targetListId]);

  const fetchItems = useCallback(async (listId, type) => {
    if (!listId) {
      if (type === 'general') setGeneralItems([]);
      else setPersonalItems([]);
      return;
    }

    try {
      if (type === 'general') setLoadingGeneralItems(true);
      else setLoadingPersonalItems(true);
      const response = await apiClient.get(`${API_BASE_URL}/checklists/${listId}/items`);
      if (type === 'general') setGeneralItems(Array.isArray(response.data) ? response.data : []);
      else setPersonalItems(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      setError(resolveChecklistError(err, 'Checklist maddeleri yüklenemedi.'));
    } finally {
      if (type === 'general') setLoadingGeneralItems(false);
      else setLoadingPersonalItems(false);
    }
  }, []);

  const fetchSuggestions = useCallback(async (activeFilters) => {
    try {
      setLoadingSuggestions(true);
      const response = await apiClient.get(`${API_BASE_URL}/checklists/suggestions`, {
        params: {
          phase_key: activeFilters.phase_key || undefined,
          discipline_key: activeFilters.discipline_key || undefined,
          q: activeFilters.q || undefined,
        },
      });
      setSuggestions(Array.isArray(response.data?.suggestions) ? response.data.suggestions : []);
      setPhaseOptions(Array.isArray(response.data?.phase_options) ? response.data.phase_options : []);
      setDisciplineOptions(Array.isArray(response.data?.discipline_options) ? response.data.discipline_options : []);
    } catch (err) {
      setError(resolveChecklistError(err, 'Öneri listesi yüklenemedi.'));
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  const fetchTaskProjects = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/projects`);
      setTaskProjects(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setTaskProjects([]);
    }
  }, []);

  const fetchTaskSyncItems = useCallback(async () => {
    try {
      setTaskSyncLoading(true);
      setTaskSyncError('');
      const params = {};
      if (taskSyncProjectId !== 'all') {
        params.project_id = taskSyncProjectId;
      }
      const response = await apiClient.get(`${API_BASE_URL}/tasks`, { params });
      const incoming = Array.isArray(response.data) ? response.data : [];
      const sorted = incoming.slice().sort((a, b) => {
        if (!a.due_date && !b.due_date) return Number(b.id) - Number(a.id);
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
      setTaskSyncOpenItems(sorted.filter((task) => !TASK_DONE_STATUSES.has(String(task.status || '').toLowerCase())));
      setTaskSyncDoneItems(sorted.filter((task) => TASK_DONE_STATUSES.has(String(task.status || '').toLowerCase())));
    } catch (err) {
      setTaskSyncOpenItems([]);
      setTaskSyncDoneItems([]);
      setTaskSyncError(err.response?.data?.message || 'Görev verileri yüklenemedi.');
    } finally {
      setTaskSyncLoading(false);
    }
  }, [taskSyncProjectId]);

  useEffect(() => {
    fetchLists();
    fetchSuggestions(INITIAL_FILTERS);
  }, [fetchLists, fetchSuggestions]);

  useEffect(() => {
    fetchItems(generalSelectedId, 'general');
  }, [generalSelectedId, fetchItems]);

  useEffect(() => {
    fetchItems(personalSelectedId, 'personal');
  }, [personalSelectedId, fetchItems]);

  useEffect(() => {
    if (activeTab !== 'task_sync') return;
    fetchTaskProjects();
    fetchTaskSyncItems();
  }, [activeTab, fetchTaskProjects, fetchTaskSyncItems]);

  const createList = async (type) => {
    const form = type === 'general' ? generalListForm : personalListForm;
    try {
      await apiClient.post(`${API_BASE_URL}/checklists`, {
        name: form.name.trim(),
        description: form.description.trim(),
        type,
      });
      if (type === 'general') setGeneralListForm({ name: '', description: '' });
      else setPersonalListForm({ name: '', description: '' });
      await fetchLists();
      setInfoMessage('Checklist oluşturuldu.');
    } catch (err) {
      setError(err.response?.data?.message || 'Checklist oluşturulamadı.');
    }
  };

  const createItem = async (type) => {
    const listId = type === 'general' ? generalSelectedId : personalSelectedId;
    const form = type === 'general' ? generalItemForm : personalItemForm;
    if (!listId) return;
    try {
      await apiClient.post(`${API_BASE_URL}/checklists/${listId}/items`, {
        title: form.title.trim(),
        category: form.category.trim() || null,
        due_date: form.due_date || null,
        priority: form.priority || 'medium',
      });
      if (type === 'general') setGeneralItemForm({ title: '', category: '', due_date: '', priority: 'medium' });
      else setPersonalItemForm({ title: '', category: '', due_date: '', priority: 'medium' });
      await Promise.all([fetchItems(listId, type), fetchLists()]);
      setInfoMessage('Madde eklendi.');
    } catch (err) {
      setError(err.response?.data?.message || 'Madde eklenemedi.');
    }
  };

  const deleteList = async (listId, type) => {
    if (!window.confirm('Bu listeyi silmek istiyor musunuz?')) return;
    try {
      await apiClient.delete(`${API_BASE_URL}/checklists/${listId}`);
      if (type === 'general') setGeneralSelectedId(null);
      else setPersonalSelectedId(null);
      await fetchLists();
      setInfoMessage('Liste silindi.');
    } catch (err) {
      setError(err.response?.data?.message || 'Liste silinemedi.');
    }
  };

  const deleteItem = async (itemId, type) => {
    if (!window.confirm('Bu maddeyi silmek istiyor musunuz?')) return;
    const listId = type === 'general' ? generalSelectedId : personalSelectedId;
    try {
      await apiClient.delete(`${API_BASE_URL}/checklists/items/${itemId}`);
      await Promise.all([fetchItems(listId, type), fetchLists()]);
    } catch (err) {
      setError(err.response?.data?.message || 'Madde silinemedi.');
    }
  };

  const toggleItem = async (item, type) => {
    const listId = type === 'general' ? generalSelectedId : personalSelectedId;
    if (!window.navigator.onLine) {
      if (type === 'general') {
        setGeneralItems((prev) => toggleChecklistItems(prev, item.id));
      } else {
        setPersonalItems((prev) => toggleChecklistItems(prev, item.id));
      }
      enqueueOfflineRequest({
        method: 'put',
        url: `${API_BASE_URL}/checklists/items/${item.id}`,
        data: { is_completed: !item.is_completed },
        label: `Checklist maddesi bekliyor: ${item.title}`,
      });
      setInfoMessage('Bağlantı yok. Checklist güncellemesi kuyruğa alındı.');
      setError('');
      return;
    }
    try {
      await apiClient.put(`${API_BASE_URL}/checklists/items/${item.id}`, { is_completed: !item.is_completed });
      await Promise.all([fetchItems(listId, type), fetchLists()]);
    } catch (err) {
      setError(err.response?.data?.message || 'Madde durumu güncellenemedi.');
    }
  };

  const importSuggestions = async () => {
    if (!targetListId) {
      setError('Önerileri aktarmak için hedef liste seçin.');
      return;
    }
    if (selectedSuggestionIds.length === 0) {
      setError('Aktarım için en az bir öneri seçin.');
      return;
    }
    try {
      const response = await apiClient.post(`${API_BASE_URL}/checklists/${targetListId}/import-suggestions`, {
        suggestion_ids: selectedSuggestionIds,
        phase_key: filters.phase_key || null,
        discipline_key: filters.discipline_key || null,
      });
      setSelectedSuggestionIds([]);
      await fetchLists();
      setInfoMessage(response.data?.message || 'Öneriler aktarıldı.');
    } catch (err) {
      setError(err.response?.data?.message || 'Öneriler aktarılamadı.');
    }
  };

  const getProjectName = (projectId) => {
    const project = taskProjects.find((item) => Number(item.id) === Number(projectId));
    return project?.name || `Proje #${projectId}`;
  };

  const toggleTaskSyncItem = async (task) => {
    if (!canToggleTaskStatus) {
      setTaskSyncError('Görev durumunu değiştirme yetkiniz bulunmuyor.');
      return;
    }
    const isDone = TASK_DONE_STATUSES.has(String(task.status || '').toLowerCase());
    const nextStatus = isDone ? 'in_progress' : 'completed';
    if (!window.navigator.onLine) {
      if (isDone) {
        setTaskSyncDoneItems((prev) => prev.filter((entry) => Number(entry.id) !== Number(task.id)));
        setTaskSyncOpenItems((prev) => [{ ...task, status: nextStatus }, ...prev]);
      } else {
        setTaskSyncOpenItems((prev) => prev.filter((entry) => Number(entry.id) !== Number(task.id)));
        setTaskSyncDoneItems((prev) => [{ ...task, status: nextStatus }, ...prev]);
      }
      enqueueOfflineRequest({
        method: 'put',
        url: `${API_BASE_URL}/tasks/${task.id}`,
        data: { status: nextStatus },
        label: `Görev durumu bekliyor: ${task.title}`,
      });
      setInfoMessage(
        isDone
          ? 'Bağlantı yok. Görev tekrar açık duruma alınmak üzere kuyruğa alındı.'
          : 'Bağlantı yok. Görev tamamlandı olarak kuyruğa alındı.'
      );
      setTaskSyncError('');
      return;
    }
    try {
      await apiClient.put(`${API_BASE_URL}/tasks/${task.id}`, { status: nextStatus });
      await fetchTaskSyncItems();
      setInfoMessage(isDone ? 'Görev tekrar açık duruma alındı.' : 'Görev tamamlandı olarak işaretlendi.');
    } catch (err) {
      setTaskSyncError(err.response?.data?.message || 'Görev durumu güncellenemedi.');
    }
  };

  const renderTaskSyncPanel = () => {
    const overdueCount = taskSyncOpenItems.filter((task) => task.due_date && new Date(task.due_date) < new Date()).length;

    return (
      <section className="checklists-panel checklist-task-sync">
        <div className="panel-title-row">
          <h2>Görev Takip</h2>
          <button type="button" onClick={fetchTaskSyncItems}>Yenile</button>
        </div>

        <p className="muted checklist-task-sync-note">
          Görevler sayfasında açılan tüm görevler burada otomatik görünür. Tamamlananlar ayrı listelenir;
          böylece ekip içi takibi tekrar veri girişi yapmadan sürdürebilirsiniz.
        </p>

        <div className="task-sync-summary">
          <div className="task-sync-box">
            <span>Açık Görevler</span>
            <strong>{taskSyncOpenItems.length}</strong>
          </div>
          <div className="task-sync-box">
            <span>Tamamlananlar</span>
            <strong>{taskSyncDoneItems.length}</strong>
          </div>
          <div className="task-sync-box">
            <span>Gecikenler</span>
            <strong>{overdueCount}</strong>
          </div>
        </div>

        <div className="checklists-form compact">
          <div className="form-row">
            <select value={taskSyncProjectId} onChange={(e) => setTaskSyncProjectId(e.target.value)}>
              <option value="all">Tüm Projeler</option>
              {taskProjects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            <button type="button" onClick={fetchTaskSyncItems}>Filtrele</button>
          </div>
        </div>

        {taskSyncError && <div className="checklists-alert error">{taskSyncError}</div>}

        {taskSyncLoading ? (
          <div className="loading-box">Görevler yükleniyor...</div>
        ) : (
          <div className="checklist-split-grid task-sync-grid">
            <section className="task-sync-list">
              <h3>Açık Görevler</h3>
              {taskSyncOpenItems.length === 0 ? (
                <p className="muted">Açık görev bulunmuyor.</p>
              ) : (
                taskSyncOpenItems.map((task) => (
                  <label key={task.id} className="task-sync-item">
                    <input type="checkbox" checked={false} onChange={() => toggleTaskSyncItem(task)} disabled={!canToggleTaskStatus} />
                    <div>
                      <strong>{task.title}</strong>
                      <div className="task-sync-meta">
                        <span>{getProjectName(task.project_id)}</span>
                        {task.due_date && <span>Son: {formatDate(task.due_date)}</span>}
                        {task.priority && <span>Öncelik: {task.priority}</span>}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </section>

            <section className="task-sync-list">
              <h3>Tamamlananlar</h3>
              {taskSyncDoneItems.length === 0 ? (
                <p className="muted">Henüz tamamlanan görev yok.</p>
              ) : (
                taskSyncDoneItems.map((task) => (
                  <label key={task.id} className="task-sync-item done">
                    <input type="checkbox" checked onChange={() => toggleTaskSyncItem(task)} disabled={!canToggleTaskStatus} />
                    <div>
                      <strong>{task.title}</strong>
                      <div className="task-sync-meta">
                        <span>{getProjectName(task.project_id)}</span>
                        {task.due_date && <span>Son: {formatDate(task.due_date)}</span>}
                        {task.priority && <span>Öncelik: {task.priority}</span>}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </section>
          </div>
        )}
      </section>
    );
  };

  const renderListPanel = (type) => {
    const isGeneral = type === 'general';
    const listTitle = isGeneral ? 'Yapılacaklar Listesi' : 'Kişisel Yapılacaklar Listesi';
    const listForm = isGeneral ? generalListForm : personalListForm;
    const setListForm = isGeneral ? setGeneralListForm : setPersonalListForm;
    const selectedList = isGeneral ? selectedGeneralList : selectedPersonalList;
    const selectedId = isGeneral ? generalSelectedId : personalSelectedId;
    const setSelectedId = isGeneral ? setGeneralSelectedId : setPersonalSelectedId;
    const itemForm = isGeneral ? generalItemForm : personalItemForm;
    const setItemForm = isGeneral ? setGeneralItemForm : setPersonalItemForm;
    const items = isGeneral ? generalItems : personalItems;
    const loadingItems = isGeneral ? loadingGeneralItems : loadingPersonalItems;
    const sourceLists = isGeneral ? generalLists : personalLists;

    return (
      <div className="checklist-split-grid">
        <section className="checklists-panel">
          <h2>{listTitle}</h2>
          <div className="checklists-form">
            <input
              type="text"
              placeholder="Liste adı"
              value={listForm.name}
              onChange={(e) => setListForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <textarea
              rows={2}
              placeholder="Açıklama (opsiyonel)"
              value={listForm.description}
              onChange={(e) => setListForm((prev) => ({ ...prev, description: e.target.value }))}
            />
            <button type="button" onClick={() => createList(type)}>Liste Oluştur</button>
          </div>

          {loadingLists ? (
            <div className="loading-box">Listeler yükleniyor...</div>
          ) : sourceLists.length === 0 ? (
            <p className="muted">Bu bölümde henüz liste yok.</p>
          ) : (
            sourceLists.map((list) => {
              const progress = list.total_items > 0 ? Math.round((list.completed_items / list.total_items) * 100) : 0;
              return (
                <button
                  key={list.id}
                  type="button"
                  className={`list-row ${Number(selectedId) === Number(list.id) ? 'active' : ''}`}
                  onClick={() => setSelectedId(list.id)}
                >
                  <div className="list-row-top">
                    <strong>{list.name}</strong>
                    <span>{progress}%</span>
                  </div>
                  <div className="list-row-meta">
                    <span>{list.completed_items}/{list.total_items} tamamlandı</span>
                    <span>{list.owner_username || list.created_by_username || '-'}</span>
                  </div>
                </button>
              );
            })
          )}
        </section>

        <section className="checklists-panel">
          <div className="panel-title-row">
            <h2>Liste Detayı</h2>
            {selectedList && (
              <button type="button" className="danger" onClick={() => deleteList(selectedList.id, type)}>
                Listeyi Sil
              </button>
            )}
          </div>

          {!selectedList ? (
            <p className="muted">Soldan bir liste seçin.</p>
          ) : (
            <>
              <div className="selected-list-head">
                <h3>{selectedList.name}</h3>
                <p>{selectedList.description || 'Açıklama yok.'}</p>
              </div>

              <div className="checklists-form compact">
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Yeni madde"
                    value={itemForm.title}
                    onChange={(e) => setItemForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                  <input
                    type="text"
                    placeholder="Kategori"
                    list={`${type}-category-suggestions`}
                    value={itemForm.category}
                    onChange={(e) => setItemForm((prev) => ({ ...prev, category: e.target.value }))}
                  />
                </div>
                <datalist id={`${type}-category-suggestions`}>
                  {categorySuggestions.map((category) => (
                    <option key={`${type}-${category}`} value={category} />
                  ))}
                </datalist>
                <div className="form-row">
                  <input
                    type="date"
                    value={itemForm.due_date}
                    onChange={(e) => setItemForm((prev) => ({ ...prev, due_date: e.target.value }))}
                  />
                  <select value={itemForm.priority} onChange={(e) => setItemForm((prev) => ({ ...prev, priority: e.target.value }))}>
                    <option value="low">Önem: Düşük</option>
                    <option value="medium">Önem: Orta</option>
                    <option value="high">Önem: Yüksek</option>
                    <option value="critical">Önem: Kritik</option>
                  </select>
                </div>
                <div className="form-row">
                  <button type="button" onClick={() => createItem(type)}>Madde Ekle</button>
                </div>
              </div>

              {loadingItems ? (
                <div className="loading-box">Maddeler yükleniyor...</div>
              ) : items.length === 0 ? (
                <p className="muted">Bu listede madde yok.</p>
              ) : (
                <div className="item-list">
                  {items.map((item) => (
                    <div key={item.id} className={`item-row ${item.is_completed ? 'done' : ''}`}>
                      <label className="item-check">
                        <input type="checkbox" checked={Boolean(item.is_completed)} onChange={() => toggleItem(item, type)} />
                        <span>{item.title}</span>
                      </label>
                      <div className="item-meta">
                        {item.category && <span className="chip">{item.category}</span>}
                        {item.due_date && <span className="chip">Son: {new Date(item.due_date).toLocaleDateString('tr-TR')}</span>}
                        {item.priority && <span className="chip">Önem: {item.priority}</span>}
                      </div>
                      <button type="button" className="danger-link" onClick={() => deleteItem(item.id, type)}>
                        Sil
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    );
  };

  return (
    <div className="checklists-page">
      <div className="checklists-header">
        <h1>Checklist Merkezi</h1>
        <p>Genel, kişisel ve öneri odaklı takip listelerini tek ekranda yönetin.</p>
      </div>

      {error && <div className="checklists-alert error">{error}</div>}
      {infoMessage && <div className="checklists-alert success">{infoMessage}</div>}

      <div className="checklist-tabs">
        <button type="button" className={activeTab === 'general' ? 'active' : ''} onClick={() => setActiveTab('general')}>
          Yapılacaklar
        </button>
        <button type="button" className={activeTab === 'personal' ? 'active' : ''} onClick={() => setActiveTab('personal')}>
          Kişisel Liste
        </button>
        <button type="button" className={activeTab === 'suggestions' ? 'active' : ''} onClick={() => setActiveTab('suggestions')}>
          Öneriler
        </button>
        <button type="button" className={activeTab === 'task_sync' ? 'active' : ''} onClick={() => setActiveTab('task_sync')}>
          Görev Takip
        </button>
      </div>

      {activeTab === 'general' && renderListPanel('general')}
      {activeTab === 'personal' && renderListPanel('personal')}
      {activeTab === 'task_sync' && renderTaskSyncPanel()}

      {activeTab === 'suggestions' && (
        <section className="checklists-panel suggestion-panel">
          <div className="panel-title-row">
            <h2>Öneriler Listesi</h2>
            <div className="suggestion-actions">
              <button type="button" onClick={() => setSelectedSuggestionIds(suggestions.map((item) => item.id))}>Tümünü Seç</button>
              <button type="button" onClick={() => setSelectedSuggestionIds([])}>Temizle</button>
            </div>
          </div>

          <div className="checklists-form compact">
            <div className="form-row">
              <select value={filters.phase_key} onChange={(e) => setFilters((prev) => ({ ...prev, phase_key: e.target.value }))}>
                <option value="">Tüm fazlar</option>
                {phaseOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
              <select value={filters.discipline_key} onChange={(e) => setFilters((prev) => ({ ...prev, discipline_key: e.target.value }))}>
                <option value="">Tüm disiplinler</option>
                {disciplineOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <input
                type="text"
                placeholder="Arama (örn: şap, kot, kablo)"
                value={filters.q}
                onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
              />
              <button type="button" onClick={() => fetchSuggestions(filters)}>Filtrele</button>
            </div>
          </div>

          <div className="suggestion-import-bar">
            <select value={targetListId} onChange={(e) => setTargetListId(e.target.value)}>
              <option value="">Hedef checklist seçin</option>
              {allLists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name} ({list.type === 'general' ? 'Genel' : 'Kişisel'})
                </option>
              ))}
            </select>
            <button type="button" onClick={importSuggestions} disabled={!targetListId || selectedSuggestionIds.length === 0}>
              Seçilenleri Listeye Aktar ({selectedSuggestionIds.length})
            </button>
          </div>

          {loadingSuggestions ? (
            <div className="loading-box">Öneriler yükleniyor...</div>
          ) : suggestions.length === 0 ? (
            <p className="muted">Filtreye uygun öneri bulunamadı.</p>
          ) : (
            <div className="suggestion-group-list">
              {[...groupedSuggestions.entries()].map(([phase, disciplineMap]) => (
                <details key={phase} open className="suggestion-group">
                  <summary>{phase}</summary>
                  {[...disciplineMap.entries()].map(([discipline, items]) => (
                    <div key={`${phase}-${discipline}`} className="suggestion-discipline">
                      <h4>{discipline}</h4>
                      {items.map((item) => (
                        <label key={item.id} className="suggestion-item-friendly">
                          <input
                            type="checkbox"
                            checked={selectedSuggestionIds.includes(item.id)}
                            onChange={() =>
                              setSelectedSuggestionIds((prev) =>
                                prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
                              )
                            }
                          />
                          <div>
                            <strong>{item.title}</strong>
                            {item.description && <p>{item.description}</p>}
                          </div>
                        </label>
                      ))}
                    </div>
                  ))}
                </details>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default Checklists;
